import axios from "axios";
import { DV_API_BASE, getSession } from "../components/DocumentVerification/auth";

export interface SubmitDocumentVerificationParams {
  verificationId: string;
  province: string;
  applicantName: string;
  photoId: File;
  rentalApplication: File;
  incomeDocuments: File[];
  creditReport?: File | null;
  bankStatements?: File[];
  employmentLetter?: File | null;
}

export interface VerificationDocumentItem {
  url: string;
  blobName: string;
  originalName: string;
  mimeType: string;
  size: number;
}

export interface DocumentVerificationResponse {
  success: boolean;
  message: string;
  data: {
    verificationId: string;
    status: string;
    province: string;
    applicantName: string;
    documents: {
      photoId: VerificationDocumentItem;
      rentalApplication: VerificationDocumentItem;
      incomeDocuments: VerificationDocumentItem[];
      creditReport: VerificationDocumentItem | null;
      bankStatements: VerificationDocumentItem[];
      employmentLetter: VerificationDocumentItem | null;
    };
    aiResult?: any;
    aiProcessedAt?: string | null;
    aiError?: string | null;
    createdAt: string;
  };
}

export async function submitDocumentVerification(
  params: SubmitDocumentVerificationParams
): Promise<DocumentVerificationResponse> {
  const session = getSession();
  if (!session?.token) {
    throw new Error("Authentication required. Please log in first.");
  }

  const formData = new FormData();
  formData.append("verificationId", params.verificationId);
  formData.append("province", params.province);
  formData.append("applicantName", params.applicantName);

  formData.append("photoId", params.photoId);
  formData.append("rentalApplication", params.rentalApplication);

  params.incomeDocuments.forEach((f) => {
    formData.append("incomeDocuments", f);
  });

  if (params.creditReport) {
    formData.append("creditReport", params.creditReport);
  }

  (params.bankStatements || []).forEach((f) => {
    formData.append("bankStatements", f);
  });

  if (params.employmentLetter) {
    formData.append("employmentLetter", params.employmentLetter);
  }

  try {
    const res = await axios.post<DocumentVerificationResponse>(
      `${DV_API_BASE}/document-verification`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${session.token}`,
          // Do NOT set Content-Type header manually; Axios automatically sets multipart/form-data with boundary
        },
      }
    );
    return res.data;
  } catch (error: any) {
    const serverMessage = error?.response?.data?.message;
    if (serverMessage) {
      throw new Error(serverMessage);
    }
    throw new Error(
      error?.message || "Failed to submit document verification. Please try again."
    );
  }
}

export type VerificationStatus =
  | "uploading"
  | "uploaded"
  | "checking"
  | "needs_review"
  | "completed"
  | "failed";

export interface PastVerificationRecord {
  _id?: string;
  verificationId: string;
  status: VerificationStatus | string;
  province: string;
  applicantName: string;
  documents: {
    photoId: VerificationDocumentItem;
    rentalApplication: VerificationDocumentItem;
    incomeDocuments: VerificationDocumentItem[];
    creditReport?: VerificationDocumentItem | null;
    bankStatements?: VerificationDocumentItem[];
    employmentLetter?: VerificationDocumentItem | null;
  };
  aiResult?: any;
  aiProcessedAt?: string | null;
  aiError?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface ListVerificationsResponse {
  success: boolean;
  data: PastVerificationRecord[];
}

export interface SingleVerificationResponse {
  success: boolean;
  data: PastVerificationRecord;
}

export async function fetchPastVerifications(): Promise<PastVerificationRecord[]> {
  const session = getSession();
  if (!session?.token) {
    throw new Error("Authentication required. Please log in first.");
  }

  try {
    const res = await axios.get<ListVerificationsResponse>(
      `${DV_API_BASE}/document-verification`,
      {
        headers: {
          Authorization: `Bearer ${session.token}`,
        },
      }
    );
    return res.data?.data || [];
  } catch (error: any) {
    const msg = error?.response?.data?.message || error?.message;
    throw new Error(msg || "Failed to load past verifications.");
  }
}

export async function fetchDocumentVerification(
  verificationId: string
): Promise<PastVerificationRecord | null> {
  const session = getSession();
  if (!session?.token) {
    throw new Error("Authentication required. Please log in first.");
  }

  try {
    const res = await axios.get<SingleVerificationResponse>(
      `${DV_API_BASE}/document-verification?verificationId=${encodeURIComponent(
        verificationId
      )}`,
      {
        headers: {
          Authorization: `Bearer ${session.token}`,
        },
      }
    );
    return res.data?.data || null;
  } catch (error: any) {
    const msg = error?.response?.data?.message || error?.message;
    throw new Error(msg || "Failed to fetch document verification.");
  }
}

export async function deleteDocumentVerification(
  verificationId: string
): Promise<{ success: boolean; message: string }> {
  const session = getSession();
  if (!session?.token) {
    throw new Error("Authentication required. Please log in first.");
  }

  try {
    const res = await axios.delete<{ success: boolean; message: string }>(
      `${DV_API_BASE}/document-verification?verificationId=${encodeURIComponent(
        verificationId
      )}`,
      {
        headers: {
          Authorization: `Bearer ${session.token}`,
        },
      }
    );
    return res.data;
  } catch (error: any) {
    const msg = error?.response?.data?.message || error?.message;
    throw new Error(msg || "Failed to delete document verification.");
  }
}

export function countVerificationDocuments(
  docs?: PastVerificationRecord["documents"]
): number {
  if (!docs) return 0;
  let count = 0;
  if (docs.photoId) count += 1;
  if (docs.rentalApplication) count += 1;
  if (Array.isArray(docs.incomeDocuments)) count += docs.incomeDocuments.length;
  if (docs.creditReport) count += 1;
  if (Array.isArray(docs.bankStatements)) count += docs.bankStatements.length;
  if (docs.employmentLetter) count += 1;
  return count;
}

